/*
 * Shopify Theme App Extension - Client Side Rewriter (Faylit.com Edition)
 * -----------------------------------------------------------------------
 * Targets images and videos served from 'faylit.com/cdn/shop' and 
 * redirects them through Bunny.net (cdn-faylit.b-cdn.net).
 */

(function () {
    // ------------------------------------------------------------------------
    // CONFIGURATION
    // ------------------------------------------------------------------------
    const CONFIG = {
        // Your Bunny.net Hostname
        BUNNY_HOSTNAME: "https://cdn-faylit.b-cdn.net",

        // The path segment to look for
        TARGET_PATH: "/cdn/shop",

        enabled: true,
        debug: false
    };

    if (!CONFIG.enabled) return;

    /**
     * Helper: Rewrites a single URL to use the Bunny CDN
     * @param {string} url
     * @returns {string}
     */
    function rewriteUrl(url) {
        if (!url) return url;

        // SECURITY CHECK: Exclude Fonts and CSS
        // If we rewrite fonts, browsers block them due to CORS (Cross-Origin) policies.
        // We explicitly ignore .woff, .woff2, .ttf, .otf, .eot, and .css here.
        if (url.match(/\.(woff|woff2|ttf|otf|eot|css)(\?|$)/i)) {
            return url;
        }

        // 1. Check if the URL belongs to faylit.com/cdn/shop (or relative /cdn/shop)
        if (url.includes(CONFIG.TARGET_PATH) && !url.includes('b-cdn.net')) {

            let newUrl = url;

            // Case A: Absolute URL (https://faylit.com/cdn/shop/...)
            if (url.includes('faylit.com')) {
                newUrl = url.replace('faylit.com', 'cdn-faylit.b-cdn.net');

                // Ensure HTTPS (Fixes //faylit.com... protocol-relative URLs)
                if (newUrl.startsWith('//')) {
                    newUrl = 'https:' + newUrl;
                }
            }
            // Case B: Relative URL (/cdn/shop/...)
            else if (url.startsWith(CONFIG.TARGET_PATH)) {
                newUrl = CONFIG.BUNNY_HOSTNAME + url;
            }

            return newUrl;
        }

        return url;
    }

    /**
     * Main Function: Scan and update assets
     */
    function processAssets() {
        // -------------------------------------------------------
        // 1. Handle Images (IMG tags)
        // -------------------------------------------------------
        const images = document.querySelectorAll('img:not([data-tr-cdn-processed])');
        images.forEach(element => {
            const img = /** @type {HTMLImageElement} */ (element);
            img.dataset.trCdnProcessed = "true";

            if (img.src) {
                const newSrc = rewriteUrl(img.src);
                if (newSrc !== img.src) {
                    img.dataset.originalSrc = img.src;
                    img.src = newSrc;

                    // Fallback
                    img.onerror = function () {
                        const target = /** @type {HTMLImageElement} */ (this);
                        if (target.dataset.originalSrc) {
                            target.src = target.dataset.originalSrc;
                            if (target.dataset.originalSrcset) {
                                target.srcset = target.dataset.originalSrcset;
                            }
                        }
                    };
                }
            }
            if (img.srcset) {
                const newSrcset = rewriteSrcset(img.srcset);
                if (newSrcset !== img.srcset) {
                    img.dataset.originalSrcset = img.srcset;
                    img.srcset = newSrcset;
                }
            }
        });

        // -------------------------------------------------------
        // 2. Handle Videos (VIDEO tags) - Mainly for 'poster'
        // -------------------------------------------------------
        const videos = document.querySelectorAll('video:not([data-tr-cdn-processed])');
        videos.forEach(element => {
            const video = /** @type {HTMLVideoElement} */ (element);
            video.dataset.trCdnProcessed = "true";

            if (video.poster) {
                const newPoster = rewriteUrl(video.poster);
                if (newPoster !== video.poster) {
                    video.dataset.originalPoster = video.poster;
                    video.poster = newPoster;
                }
            }
        });

        // -------------------------------------------------------
        // 3. Handle Sources (SOURCE tags inside Video or Picture)
        // -------------------------------------------------------
        const sources = document.querySelectorAll('source:not([data-tr-cdn-processed])');
        sources.forEach(element => {
            const source = /** @type {HTMLSourceElement} */ (element);
            source.dataset.trCdnProcessed = "true";

            if (source.src) {
                const newSrc = rewriteUrl(source.src);
                if (newSrc !== source.src) {
                    source.dataset.originalSrc = source.src;
                    source.src = newSrc;

                    // Fallback
                    source.onerror = function () {
                        const target = /** @type {HTMLSourceElement} */ (this);
                        if (target.dataset.originalSrc) target.src = target.dataset.originalSrc;
                    };
                }
            }
            if (source.srcset) {
                const newSrcset = rewriteSrcset(source.srcset);
                if (newSrcset !== source.srcset) {
                    source.dataset.originalSrcset = source.srcset;
                    source.srcset = newSrcset;
                }
            }
        });
    }

    /**
     * Helper to process srcset strings
     * @param {string} srcsetString
     * @returns {string}
     */
    function rewriteSrcset(srcsetString) {
        return srcsetString.split(',').map(entry => {
            const parts = entry.trim().split(/\s+/);
            const url = parts[0];
            const descriptor = parts.slice(1).join(' ');

            if (url && (url.includes(CONFIG.TARGET_PATH) || url.includes('faylit.com'))) {
                const newUrl = rewriteUrl(url);
                return descriptor ? `${newUrl} ${descriptor}` : newUrl;
            }
            return entry;
        }).join(', ');
    }

    // ------------------------------------------------------------------------
    // INITIALIZATION
    // ------------------------------------------------------------------------
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', processAssets);
    } else {
        processAssets();
    }

    const observer = new MutationObserver((mutations) => {
        let shouldProcess = false;
        for (const mutation of mutations) {
            // Check for added nodes
            if (mutation.addedNodes.length > 0) {
                shouldProcess = true;
                break;
            }
            // Check for attribute changes on relevant tags
            if (mutation.type === 'attributes') {
                const target = /** @type {HTMLElement} */ (mutation.target);
                const tagName = target.tagName;
                const attr = mutation.attributeName;

                if (tagName === 'IMG' && (attr === 'src' || attr === 'srcset')) shouldProcess = true;
                else if (tagName === 'VIDEO' && attr === 'poster') shouldProcess = true;
                else if (tagName === 'SOURCE' && (attr === 'src' || attr === 'srcset')) shouldProcess = true;

                if (shouldProcess) break;
            }
        }
        if (shouldProcess) processAssets();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'srcset', 'poster']
    });

    console.log("🇹🇷 Turkish CDN (Assets) Active");
})();
